// src/services/api.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082/api';
export const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || 'http://localhost:8082/images';
const PATIENT_IMAGE_VERSION = 'sora-20260820';

export const getPatientImageUrl = (caseId) => `${IMAGE_BASE_URL}/${caseId}.png?v=${PATIENT_IMAGE_VERSION}`;

// Helper to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
};

// Helper to create headers with auth
const getHeaders = (includeAuth = true) => {
  const headers = {
    'Content-Type': 'application/json'
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

export const apiService = {
  // Authentication endpoints
  async register(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: getHeaders(false),
        body: JSON.stringify(userData)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Registration failed');
      return result;
    } catch (error) {
      console.error('Error during registration:', error);
      throw error;
    }
  },

  async login(credentials) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: getHeaders(false),
        body: JSON.stringify(credentials)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Login failed');
      return result;
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  },

  async getCurrentUser() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: getHeaders(true)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to get user info');
      return result;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  },

  // Protected endpoints
  async getCases(language = 'en') {
    try {
      const response = await fetch(`${API_BASE_URL}/cases?language=${encodeURIComponent(language)}`, {
        headers: getHeaders(true)
      });
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication expired');
      }
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to fetch cases');
      return result;
    } catch (error) {
      console.error('Error fetching cases:', error);
      throw error;
    }
  },

  async sendChatMessage(caseId, message, phase = 'Anamnesis', transcript = [], language = 'en') {
    try {
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ case_id: caseId, message, phase, transcript, language })
      });
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication expired');
      }
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to send message');
      return result;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  async evaluateDiagnosis(caseId, diagnosis, managementPlan, transcript = [], language = 'en') {
    try {
      const response = await fetch(`${API_BASE_URL}/evaluate`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({
          case_id: caseId,
          diagnosis,
          management_plan: managementPlan,
          transcript,
          language
        })
      });
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication expired');
      }
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to evaluate diagnosis');
      return result;
    } catch (error) {
      console.error('Error evaluating diagnosis:', error);
      throw error;
    }
  },

  async translateTexts(texts = [], language = 'en', options = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}/translate-texts`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ texts, language, strict: Boolean(options.strict) })
      });
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication expired');
      }
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to translate feedback');
      return result;
    } catch (error) {
      console.error('Error translating feedback:', error);
      throw error;
    }
  },

  async getMySubmissions() {
    try {
      const response = await fetch(`${API_BASE_URL}/submissions/mine`, {
        headers: getHeaders(true)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to fetch your submissions');
      return result.data ?? result;
    } catch (error) {
      console.error('Error fetching my submissions:', error);
      throw error;
    }
  },

  async getDoctorCases() {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-review/cases`, {
        headers: getHeaders(true)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to fetch cases');
      return result.data ?? result;
    } catch (error) {
      console.error('Error fetching doctor cases:', error);
      throw error;
    }
  },

  async getCaseLabs(caseId, language = 'en') {
    try {
      const response = await fetch(`${API_BASE_URL}/cases/${encodeURIComponent(caseId)}/labs?language=${encodeURIComponent(language)}`, {
        headers: getHeaders(true)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to fetch lab reports');
      return result.data ?? result;
    } catch (error) {
      console.error('Error fetching case lab reports:', error);
      throw error;
    }
  },

  async createCase(caseData) {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-review/cases`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify(caseData)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to create case');
      return result.data ?? result;
    } catch (error) {
      console.error('Error creating case:', error);
      throw error;
    }
  },

  async updateCase(caseId, caseData) {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-review/cases/${encodeURIComponent(caseId)}`, {
        method: 'PUT',
        headers: getHeaders(true),
        body: JSON.stringify(caseData)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to update case');
      return result.data ?? result;
    } catch (error) {
      console.error('Error updating case:', error);
      throw error;
    }
  },

  async getCaseDetail(caseId) {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-review/cases/${caseId}`, {
        headers: getHeaders(true)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to fetch case');
      return result.data ?? result;
    } catch (error) {
      console.error('Error fetching case detail:', error);
      throw error;
    }
  },

  async getSubmission(submissionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-review/submissions/${submissionId}`, {
        headers: getHeaders(true)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to fetch submission');
      return result.data ?? result;
    } catch (error) {
      console.error('Error fetching submission:', error);
      throw error;
    }
  },

  async getSubmissions() {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-review/submissions`, {
        headers: getHeaders(true)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to fetch submissions');
      return result.data ?? result;
    } catch (error) {
      console.error('Error fetching submissions:', error);
      throw error;
    }
  },

  async submitDoctorScore(submissionId, historyScore, diagnosisScore, managementScore, comment = '') {
    try {
      const response = await fetch(
        `${API_BASE_URL}/doctor-review/submissions/${submissionId}/score`,
        {
          method: 'POST',
          headers: getHeaders(true),
          body: JSON.stringify({
            doctor_score_history: historyScore,
            doctor_score_diagnosis: diagnosisScore,
            doctor_score_management: managementScore,
            doctor_comment: comment
          })
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to submit score');
      return result.data ?? result;
    } catch (error) {
      console.error('Error submitting doctor score:', error);
      throw error;
    }
  },

  async getPatientImage(caseId) {
    try {
      const response = await fetch(`${API_BASE_URL}/patient-image/${caseId}`, {
        headers: getHeaders(true)
      });
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication expired');
      }
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to get patient image');
      return result;
    } catch (error) {
      console.error('Error fetching patient image:', error);
      throw error;
    }
  },

  async startSession(caseId) {
    try {
      const response = await fetch(`${API_BASE_URL}/session/start`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ caseId })
      });
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication expired');
      }
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to start session');
      return result;
    } catch (error) {
      console.error('Error starting session:', error);
      throw error;
    }
  }
};
